(function () {
  "use strict";

  // Custom element definition for 3D product models
  class ProductModelElement extends window.theme.DeferredMedia {
    constructor() {
      super();
    }

    loadContent() {
      super.loadContent();

      // Load model-viewer UI features
      Shopify.loadFeatures([
        {
          name: "model-viewer-ui",
          version: "1.0",
          onLoad: this.setupModelViewerUI.bind(this),
        },
      ]);
    }

    setupModelViewerUI(success) {
      if (!success) {
        this.modelViewerUI = new Shopify.ModelViewerUI(
          this.querySelector("model-viewer")
        );
      }
    }
  }

  // Shopify XR (Augmented Reality) product model loading logic
  window.ProductModel = {
    loadShopifyXR() {
      Shopify.loadFeatures([
        {
          name: "shopify-xr",
          version: "1.0",
          onLoad: this.setupShopifyXR.bind(this),
        },
      ]);
    },

    setupShopifyXR(success) {
      if (!success) {
        if (window.ShopifyXR) {
          document
          .querySelectorAll('[id^="ModelJSON-"]')
          .forEach((element) => {
            window.ShopifyXR.addModels(JSON.parse(element.textContent));
            element.remove();
          });
          window.ShopifyXR.setupXRElements();
        } else {
          document.addEventListener("shopify_xr_initialized", () =>
            this.setupShopifyXR()
          );
        }
      }
    },
  };

  // Load XR models on page load
  window.addEventListener("DOMContentLoaded", () => {
    if (window.ProductModel) {
      window.ProductModel.loadShopifyXR();
    }
  });

  // Constants
  const SELECTORS = {
    addToCart: "[data-add-to-cart]",
    productJson: "[data-product-json]",
    eventProductJson: "[data-event-product-json]",
    productForm: "[data-product-form]",
    cartBar: "#cart-bar",
    popupOpen: "[data-popup-open]",
    cartBarAddBtn: ".product__submit__add",
    formWrapper: "[data-form-wrapper]",
    variants: "[data-product-variants]",
    quantityInput: ".quantity__input",
    productFormQuantityInput: ".product__content .quantity__input",
  };

  const ATTRIBUTES = {
    cartBarEnabled: "data-cart-bar-enabled",
    addToCartBar: "data-add-to-cart-bar",
    scrollToTop: "data-cart-bar-scroll",
    productNotification: "data-cart-bar-product-notification",
    stickyEnabled: "data-sticky-enabled",
  };

  const CLASSES = {
    loading: "is-loading",
    visible: "is-visible",
  };

  // Define product-component if not already defined
  if (!customElements.get("product-component")) {
    customElements.define(
      "product-component",
      class extends HTMLElement {
        constructor() {
          super();
          this.stickyEnabled =
            this.getAttribute(ATTRIBUTES.stickyEnabled) === "true";
          this.formWrapper = this.querySelector(SELECTORS.formWrapper);
          this.cartBarEnabled = this.hasAttribute(ATTRIBUTES.cartBarEnabled);
          this.cartBar = this.querySelector(SELECTORS.cartBar);

          this.setCartBarHeight = this.setCartBarHeight.bind(this);
          this.scrollToTop = this.scrollToTop.bind(this);
          this.toggleCartBarOnScroll = this.toggleCartBarOnScroll.bind(this);
        }

        connectedCallback() {
          const productJsonElement = this.querySelector(SELECTORS.productJson);
          if (!productJsonElement || !productJsonElement.innerHTML) return;

          const productData = JSON.parse(productJsonElement.innerHTML);
          const handle = productData.handle;

          if (handle) {
            const productInfo = { handle };
            Shopify.Products.recordRecentlyViewed(productInfo);
          }

          this.form = this.querySelector(SELECTORS.productForm);

          if (this.cartBarEnabled) {
            this.initCartBar();
            this.setCartBarHeight();
            document.addEventListener("theme:scroll", this.toggleCartBarOnScroll);
            document.addEventListener("theme:resize", this.setCartBarHeight);
          }

          // Start polling for product availability
          this.startAvailabilityPolling();
        }

        initCartBar() {
          this.cartBarBtns = this.cartBar.querySelectorAll(SELECTORS.cartBarAddBtn);
          this.quantityInput = this.cartBar.querySelector(SELECTORS.quantityInput);
          this.productFormQuantityInput = document.querySelector(SELECTORS.productFormQuantityInput);


          this.cartBarBtns.forEach((button) => {
            button.addEventListener("click", (event) => {
              event.preventDefault();

              if (button.hasAttribute(ATTRIBUTES.addToCartBar)) {
                if (this.cartBarEnabled) {
                  button.classList.add(CLASSES.loading);
                  button.setAttribute("disabled", "disabled");
                }

                // Trigger native form Add to Cart button
                this.form
                .querySelector(SELECTORS.addToCart)
                .dispatchEvent(new Event("click", { bubbles: true }));
              } else if (button.hasAttribute(ATTRIBUTES.scrollToTop)) {
                this.scrollToTop();
              } else if (button.hasAttribute(ATTRIBUTES.productNotification)) {
                this.form
                .querySelector(SELECTORS.popupOpen)
                ?.dispatchEvent(new Event("click"));
              }
            });

            // Scroll to top on add error
            if (button.hasAttribute(ATTRIBUTES.addToCartBar)) {
              document.addEventListener("theme:product:add-error", this.scrollToTop);
            }
          });

          if (this.quantityInput && this.productFormQuantityInput) {
            this.quantityInput.addEventListener('change', (event) => {
              this.productFormQuantityInput.value = event.target.value;
            });

            this.productFormQuantityInput.addEventListener('change', (event) => {
              this.quantityInput.value = event.target.value;
            });
          }



          this.setCartBarHeight();
        }

        scrollToTop() {
          const variantSection = this.querySelector(SELECTORS.variants);
          const scrollTarget = window.theme.isMobile()
            ? variantSection || this.form
            : this;

          const topOffset = scrollTarget.getBoundingClientRect().top;
          window.theme.scrollTo(window.theme.isMobile() ? topOffset - 10 : topOffset);
        }

        toggleCartBarOnScroll() {
          const scrollY = window.scrollY;
          const container =
            theme.variables.productPageSticky && this.formWrapper
              ? this.formWrapper
              : this.form;

          const parentContainer = container.closest(".product__content");

          if (parentContainer && this.cartBar) {
            const isPastForm =
              scrollY > parentContainer.offsetTop + parentContainer.offsetHeight - (window.innerHeight / 2);
            this.cartBar.classList.toggle(CLASSES.visible, isPastForm);
          }
        }

        setCartBarHeight() {
          const height = this.cartBar.offsetHeight;
          document.documentElement.style.setProperty("--cart-bar-height", `${height}px`);
        }

        /**
         * Polls the product endpoint every minute to check availability.
         * If unavailable, shows a message and reloads the page.
         */
        startAvailabilityPolling() {
          if (this._availabilityPollingInterval) return; // Prevent multiple intervals
          const productJsonElement = this.querySelector(SELECTORS.eventProductJson);
          if (!productJsonElement || !productJsonElement.innerHTML) return;
          const productData = JSON.parse(productJsonElement.innerHTML);
          const handle = productData.handle;
          if (!handle) return;

          const checkAvailability = async () => {
            try {
              // Fetch the product page HTML
              const res = await fetch(`/products/${handle}`, { cache: 'no-store' });
              if (!res.ok) throw new Error('Network error');
              const html = await res.text();
              // Check if <body> contains data-template="product.event.expired"
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const body = doc.querySelector('body');
              if (body && body.getAttribute('data-template') === 'product.event.expired') {
                window.location.reload();
              }
            } catch (e) {
              // Optionally handle error
            }
          };

          // Initial check
          checkAvailability();
          // Poll every minute
          this._availabilityPollingInterval = setInterval(checkAvailability, 60000);
        }

        disconnectedCallback() {
          document.removeEventListener("theme:product:add-error", this.scrollToTop);

          if (this.cartBarEnabled) {
            document.removeEventListener("theme:scroll", this.toggleCartBarOnScroll);
            document.removeEventListener("theme:resize", this.setCartBarHeight);
          }
          // Clear polling interval if component is removed
          if (this._availabilityPollingInterval) {
            clearInterval(this._availabilityPollingInterval);
            this._availabilityPollingInterval = null;
          }
        }
      }
    );
  }

  // Define product-model if not already defined
  if (!customElements.get("product-model")) {
    customElements.define("product-model", ProductModelElement);
  }
})();
